import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, Search, HardDrive, AlertCircle, Download } from 'lucide-react';
import { Button } from '../ui/button';

interface SystemLogsTabProps {
  token: string;
}

interface SystemLogRow {
  id: string;
  event_type: string;
  severity: string;
  message: string;
  route: string | null;
  method: string | null;
  status_code: number | null;
  ip_address: string | null;
  client_id: string | null;
  bridge_id: string | null;
  metadata: Record<string, any>;
  created_at: string;
}

const fmtDate = (d: string) =>
  new Date(d).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

const severityClass = (severity: string) => {
  if (severity === 'error') return 'bg-red-500/10 text-red-500 border-red-500/30';
  if (severity === 'warn' || severity === 'warning') return 'bg-amber-500/10 text-amber-500 border-amber-500/30';
  return 'bg-blue-500/10 text-blue-500 border-blue-500/30';
};

const csvEscape = (value: unknown) => {
  const text = String(value ?? '');
  if (text.includes(',') || text.includes('"') || text.includes('\n')) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

export function SystemLogsTab({ token }: SystemLogsTabProps) {
  const [logs, setLogs] = useState<SystemLogRow[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [severity, setSeverity] = useState('all');
  const [clientId, setClientId] = useState('all');
  const [retentionDays, setRetentionDays] = useState<number>(2);
  const [exporting, setExporting] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchClients = useCallback(async () => {
    try {
      const res = await fetch('/api/clients', { headers });
      if (!res.ok) return;
      const data = await res.json();
      setClients(data.clients || []);
    } catch {
      // Non-blocking.
    }
  }, [headers]);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      if (severity !== 'all') params.set('severity', severity);
      if (clientId !== 'all') params.set('clientId', clientId);
      params.set('limit', '500');
      params.set('offset', '0');

      const res = await fetch(`/api/admin/system-logs?${params.toString()}`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setLogs(data.logs || []);
      if (typeof data.retentionDays === 'number') {
        setRetentionDays(data.retentionDays);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load system logs');
    } finally {
      setLoading(false);
    }
  }, [headers, search, severity, clientId]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const downloadFile = (filename: string, content: string, mimeType: string) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  const exportJson = async () => {
    try {
      setExporting(true);
      const stamp = new Date().toISOString().replace(/[:.]/g, '-');
      downloadFile(`system-logs-${stamp}.json`, JSON.stringify(logs, null, 2), 'application/json;charset=utf-8');
    } finally {
      setExporting(false);
    }
  };

  const exportCsv = async () => {
    try {
      setExporting(true);
      const header = [
        'id',
        'created_at',
        'severity',
        'event_type',
        'message',
        'method',
        'route',
        'status_code',
        'client_id',
        'bridge_id',
        'ip_address',
        'metadata',
      ];

      const rows = logs.map((log) => [
        log.id,
        log.created_at,
        log.severity,
        log.event_type,
        log.message,
        log.method || '',
        log.route || '',
        log.status_code ?? '',
        log.client_id || '',
        log.bridge_id || '',
        log.ip_address || '',
        JSON.stringify(log.metadata || {}),
      ]);

      const csv = [header, ...rows]
        .map((row) => row.map((cell) => csvEscape(cell)).join(','))
        .join('\n');

      const stamp = new Date().toISOString().replace(/[:.]/g, '-');
      downloadFile(`system-logs-${stamp}.csv`, csv, 'text/csv;charset=utf-8');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
            <HardDrive className="h-5 w-5 text-blue-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">System Logs</h1>
            <p className="text-sm text-muted-foreground">
              Retention policy: {retentionDays} days
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportJson} className="gap-2" disabled={exporting || logs.length === 0}>
            <Download className="h-4 w-4" /> Export JSON
          </Button>
          <Button variant="outline" size="sm" onClick={exportCsv} className="gap-2" disabled={exporting || logs.length === 0}>
            <Download className="h-4 w-4" /> Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={fetchLogs} className="gap-2" disabled={exporting}>
            <RefreshCw className="h-4 w-4" /> Refresh
          </Button>
        </div>
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center gap-3">
          <AlertCircle className="h-4 w-4 text-red-500 shrink-0" />
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-0 sm:min-w-56">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search message, route, event type, bridge id..."
            className="w-full pl-9 pr-4 py-2 rounded-lg bg-background border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-sm"
          />
        </div>

        <select
          value={severity}
          onChange={(e) => setSeverity(e.target.value)}
          className="px-3 py-2 rounded-lg bg-background border border-border text-sm"
        >
          <option value="all">All Severity</option>
          <option value="info">Info</option>
          <option value="warn">Warn</option>
          <option value="error">Error</option>
        </select>

        <select
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          className="px-3 py-2 rounded-lg bg-background border border-border text-sm"
        >
          <option value="all">All Clients</option>
          {clients.map((client) => (
            <option key={client.id} value={client.id}>{client.name}</option>
          ))}
        </select>
      </div>

      <div className="rounded-xl border border-border overflow-hidden bg-card">
        <div className="overflow-auto max-h-[65vh]">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted/70 backdrop-blur">
              <tr className="text-left">
                <th className="px-3 py-2">Time</th>
                <th className="px-3 py-2">Severity</th>
                <th className="px-3 py-2">Event</th>
                <th className="px-3 py-2">Message</th>
                <th className="px-3 py-2">Route</th>
                <th className="px-3 py-2">Client</th>
                <th className="px-3 py-2">Bridge</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="px-3 py-6 text-muted-foreground" colSpan={7}>Loading logs...</td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td className="px-3 py-6 text-muted-foreground" colSpan={7}>No logs found.</td>
                </tr>
              ) : logs.map((log) => (
                <tr key={log.id} className="border-t border-border/60">
                  <td className="px-3 py-2 whitespace-nowrap">{fmtDate(log.created_at)}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${severityClass(log.severity)}`}>
                      {log.severity}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">{log.event_type}</td>
                  <td className="px-3 py-2 min-w-[14rem] sm:min-w-80">{log.message}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{log.method || '-'} {log.route || '-'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{log.client_id || '-'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{log.bridge_id || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
